import { type FieldValidator } from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorCodes"
import { restrictedZip } from "@/data/pfas-zipcode-state-restrictions"
import { SHIPPING_COUNTRY_CODES } from "@/lib/siteconfig/sitemap-siteconfig"
import { containsPoBox } from "@/src/utils/address/poBox"

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const BASE_SPECIAL_CHARACTERS = '!@#$%^&*()?":{}|<>[]'

/**
 * The abbreviated form. Anchored, so it stays here rather than in the shared rule: it is
 * only a PO box when it opens the address line, which is true of a single field and not of
 * the whole address joined together.
 */
const PO_NUMBER_PATTERN = /^\s*P\.?\s*O\.?\s*\d/i

const JAPANESE_PATTERN =
    /[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF]/
const CHINESE_PATTERN = /[\u4e00-\u9fff\u3400-\u4dbf\u3000-\u303f\uff00-\uffef]/
const PERSIAN_PATTERN =
    /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\u08A0-\u08FF]/

const US_ZIP_PATTERN = /^\d{5}$/
const ZIP_LETTER_PATTERN = /[a-zA-Z]/

const NO_LETTER_ZIP_COUNTRIES = new Set<string>([
    SHIPPING_COUNTRY_CODES.Austria,
    SHIPPING_COUNTRY_CODES.Germany
])

/** Blank is the required check's business, never a format complaint. */
const ifPresent =
    (validate: FieldValidator): FieldValidator =>
    (value, context) => {
        const trimmed = value.trim()
        return trimmed ? validate(trimmed, context) : null
    }

export const poBoxValidator: FieldValidator = ifPresent(value =>
    containsPoBox(value) || PO_NUMBER_PATTERN.test(value) ? "po_box_not_allowed" : null
)

/**
 * Built once per allowed set rather than once per keystroke: the pattern depends only on
 * which characters the field permits, and the checkout has two such sets.
 */
const disallowedPatterns = new Map<string, RegExp>()

const disallowedPattern = (allowedCharacters: string) => {
    const cached = disallowedPatterns.get(allowedCharacters)
    if (cached) return cached

    const disallowed = BASE_SPECIAL_CHARACTERS.split("")
        .filter(character => !allowedCharacters.includes(character))
        .join("")
    const pattern = new RegExp(`[${escapeRegExp(disallowed)}]`)

    disallowedPatterns.set(allowedCharacters, pattern)
    return pattern
}

/** Runs on every field by default, which is why the allowed set is per-field. */
export const characterValidator: FieldValidator = ifPresent(
    (value, { allowedCharacters }) => {
        if (
            JAPANESE_PATTERN.test(value) ||
            CHINESE_PATTERN.test(value) ||
            PERSIAN_PATTERN.test(value)
        ) {
            return "special_characters_not_allowed"
        }

        return disallowedPattern(allowedCharacters ?? "").test(value)
            ? "special_characters_not_allowed"
            : null
    }
)

/**
 * `restrictedZip` reads as a restriction but answers the opposite question — whether the
 * code belongs to the state — so a false is a mismatch.
 */
export const createUsZipCodeValidator = (state: string): FieldValidator =>
    ifPresent((value, { countryCode }) => {
        if (countryCode?.toLowerCase() !== SHIPPING_COUNTRY_CODES.USA) return null
        if (!US_ZIP_PATTERN.test(value)) return "zip_code_format_invalid"
        return restrictedZip(state, value) ? null : "zip_code_state_mismatch"
    })

/** Austria and Germany: letters are not part of a postcode (`D-07338`, `A-1010`). */
export const noLettersZipCodeValidator: FieldValidator = ifPresent(
    (value, { countryCode }) => {
        const country = countryCode?.toLowerCase()
        if (!country || !NO_LETTER_ZIP_COUNTRIES.has(country)) return null

        return ZIP_LETTER_PATTERN.test(value) ? "zip_code_format_invalid" : null
    }
)

/**
 * Centra takes the street and the apartment as one line, so the limit is on the pair even
 * though only one of them is being typed. The joining space `mergeAddressLine` inserts is
 * counted when both sides are present.
 */
export const createCombinedLengthValidator = (
    getCompanionValue: () => string,
    limit: number
): FieldValidator =>
    ifPresent(value => {
        const companion = getCompanionValue()
        const joiner = value && companion ? 1 : 0
        return value.length + companion.length + joiner > limit
            ? "address_too_long"
            : null
    })
